/* eslint-disable react-refresh/only-export-components */
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons"
import { faXmark } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { cva, type VariantProps } from "class-variance-authority"
import {
  useSyncExternalStore,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from "react"
import {
  toast as sonnerToast,
  Toaster as Sonner,
  type ExternalToast,
  type ToasterProps as SonnerToasterProps,
} from "sonner"
import { cn } from "cn"

// Figma „shadow-xl" des Toasts ist identisch mit dem Popover-Schatten
// (#3E3A32, 0/8/10/-6 + 0/2/25/-5) — daher `shadow-popover` wiederverwendet.
const toastVariants = cva(
  "flex w-full items-start gap-2 rounded-xl bg-backgrounds-primary p-4 text-labels-primary shadow-popover [&>svg]:pointer-events-none [&>svg]:size-6 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        success: "[&>svg]:text-accent-green-100",
        warning: "[&>svg]:text-accent-orange-100",
        error: "[&>svg]:text-error-red-200",
        information: "[&>svg]:text-indigo-100",
      },
    },
    defaultVariants: {
      variant: "success",
    },
  }
)

const toastIconByVariant = {
  success: faCircleCheck,
  warning: faTriangleExclamation,
  error: faCircleExclamation,
  information: faCircleInfo,
} as const

export type ToastVariant = NonNullable<
  VariantProps<typeof toastVariants>["variant"]
>

export type ToastProps = Omit<ComponentProps<"div">, "title"> &
  VariantProps<typeof toastVariants> & {
    title?: ReactNode
    description?: ReactNode
    showTitle?: boolean
    showDescription?: boolean
    /** Klick auf den Schließen-Button. */
    onClose?: () => void
  }

/**
 * Reine Darstellung des Figma-Toasts. In der App nicht direkt rendern,
 * sondern über `toast()` + `<Toaster />` anzeigen.
 */
function Toast({
  className,
  variant = "success",
  title,
  description,
  showTitle = true,
  showDescription = true,
  onClose,
  ...props
}: ToastProps) {
  const hasTitle = showTitle && title !== undefined && title !== null
  const hasDescription =
    showDescription && description !== undefined && description !== null

  return (
    <div
      data-slot="toast"
      data-variant={variant}
      className={cn(toastVariants({ variant, className }))}
      {...props}
    >
      <FontAwesomeIcon
        data-slot="toast-icon"
        icon={toastIconByVariant[variant ?? "success"]}
      />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <div className="min-w-0 flex-1 text-sm leading-5 break-words">
          {hasTitle && <p className="font-semibold">{title}</p>}
          {hasDescription && <p className="font-normal">{description}</p>}
        </div>
        <button
          type="button"
          data-slot="toast-close-button"
          onClick={onClose}
          className="flex size-6 shrink-0 items-center justify-center rounded p-px text-neutrals-v2-700 outline-none focus-visible:ring-2 focus-visible:ring-neutrals-v2-600 focus-visible:ring-offset-2"
        >
          <FontAwesomeIcon icon={faXmark} className="size-5.5" />
          <span className="sr-only">Close</span>
        </button>
      </div>
    </div>
  )
}

export type ToastOptions = Omit<ToastProps, "onClose"> &
  Pick<ExternalToast, "id" | "duration" | "onDismiss" | "onAutoClose">

/** Zeigt einen V2-Toast an und gibt dessen ID zurück (für `toast.dismiss`). */
function toast({
  id,
  duration,
  onDismiss,
  onAutoClose,
  ...toastProps
}: ToastOptions) {
  return sonnerToast.custom(
    (toastId) => (
      <Toast {...toastProps} onClose={() => sonnerToast.dismiss(toastId)} />
    ),
    // sonner gibt unstyled/custom Toasts keine Breite — `w-full` zieht das
    // <li> auf die Toaster-Breite (`--width` bzw. Mobile-Vollbreite).
    { id, duration, onDismiss, onAutoClose, className: "w-full" }
  )
}

toast.dismiss = sonnerToast.dismiss

// Entspricht dem Mobile-Breakpoint von sonner (600px).
const MOBILE_QUERY = "(max-width: 599px)"

function subscribeMobile(onChange: () => void) {
  const mql = window.matchMedia(MOBILE_QUERY)
  mql.addEventListener("change", onChange)
  return () => mql.removeEventListener("change", onChange)
}

function useIsMobile() {
  return useSyncExternalStore(
    subscribeMobile,
    () => window.matchMedia(MOBILE_QUERY).matches,
    () => false
  )
}

export type ToasterProps = SonnerToasterProps

/**
 * Container für V2-Toasts. Einmal pro App rendern.
 *
 * Platzierung laut Figma: Desktop unten mittig mit 16px Abstand, Mobile oben
 * unter dem Header — die Header-Höhe über `mobileOffset` setzen.
 */
function Toaster({ position, style, ...props }: ToasterProps) {
  const isMobile = useIsMobile()

  return (
    <Sonner
      position={position ?? (isMobile ? "top-center" : "bottom-center")}
      offset={16}
      mobileOffset={16}
      style={
        {
          // Figma-Breite des Toasts
          "--width": "328px",
          // Radix Dialog setzt body { pointer-events: none } — Toasts müssen
          // auch über einem offenen Sheet/Dialog klickbar bleiben.
          pointerEvents: "auto",
          ...style,
        } as CSSProperties
      }
      {...props}
    />
  )
}

export { Toast, Toaster, toast, toastVariants }
